<template>
  <div class="help-content flex flex-column">
    <vz-collapse-info
      v-for="(item, index) in items"
      :key="`${mode}-${index}`"
      :itemscope="hasItempropQuestion(item.component) ? '' : null"
      :itemtype="hasItempropQuestion(item.component) ? 'https://schema.org/Question' : null"
    >
      <template #header>
        <span :itemprop="hasItempropQuestion(item.component) ? 'name' : null">
          {{ item.header }}
        </span>
      </template>
      <component
        v-if="item.component"
        :is="item.component"
        :param="item.param"
      />
    </vz-collapse-info>
  </div>
</template>

<script setup lang="ts">
import VzCollapseInfo from '~~/components/ui-kit/vz-collapse-info.vue'
import HelpText from '~~/components/public/help/help-text.vue'
import HelpList from '~~/components/public/help/help-list.vue'
import HelpDocuments from '~~/components/public/help/help-documents.vue'
import HelpServices from '~~/components/public/help/help-services.vue'
import HelpWrapping from '~~/components/public/help/help-wrapping.vue'
import { getDocuments } from '~~/api/document'
import useMenuItems from "~/composables/useMenuItems";

const props = defineProps({
  mode: {
    type: String,
    default: 'corporate'
  }
})

const {isHideInsurance} = useMenuItems()
const dangerousCargoDoc = ref('')

const corporateDeliveryData = [
  {
    title: 'Юридическим лицам:',
    list: [
      {
        text: 'Доверенность, подтверждающая полномочия, полученные от юридического лица'
      },
      {
        text: 'Паспорт'
      },
      {
        text: 'Документы, определяющие характер груза (ТН, ТТН, сертификат)'
      }
    ]
  },
  {
    title: 'Единоличному исполнительному органу юридического лица (генеральному директору или директору компании):',
    list: [
      {
        text: 'Документ о назначении на должность единоличного исполнительного органа юридического лица (Протокол об избрании) и копия документа'
      },
      {
        text: 'Паспорт'
      },
      {
        text: 'Документы, определяющие характер груза'
      }
    ]
  },
  {
    title: 'Индивидуальному предпринимателю:',
    list: [
      {
        text: 'Свидетельство о государственной регистрации ФЛ в качестве ИП и копия документа'
      },
      {
        text: 'Паспорт'
      },
      {
        text: 'Документы, определяющие характер груза'
      }
    ]
  }
]

const corporateReceiptData = [
  {
    title: 'Юридическим лицам:',
    list: [
      {
        text: 'Код получения <div class="help-list-item">или</div>'
      },
      {
        text: 'Доверенность, либо заверенная надлежащим образом копия (указанный в доверенности ИНН организации должен совпадать с ИНН Получателя в заказе. Срок доверенности должен быть актуальным на дату выдачи груза). ',
        link: {
          title: 'Образец доверенности.',
          to: '/documents/'
        }
      },
      {
        text: 'Паспорт'
      }
    ]
  },
  {
    title: 'Единоличному исполнительному органу юридического лица (генеральному директору или директору компании):',
    list: [
      {
        text: 'Код получения <div class="help-list-item">или</div>'
      },
      {
        text: 'Документ о назначении на должность единоличного исполнительного органа юридического лица (Протокол об избрании) и копия документа'
      },
      {
        text: 'Паспорт'
      }
    ]
  },
  {
    title: 'Индивидуальному предпринимателю:',
    list: [
      {
        text: 'Код получения <div class="help-list-item">или</div>'
      },
      {
        text: 'Свидетельство о государственной регистрации ФЛ в качестве ИП и копия документа'
      },
      {
        text: 'Паспорт'
      }
    ]
  }
]

const individualDeliveryData = [
  {
    text: 'Документ, удостоверяющий личность'
  },
  {
    text: 'Опись груза (составление возможно при водителе/приёмосдатчике)'
  }
]

const claimItem = {
  header: 'Как подать претензию?',
  component: HelpText,
  param: {
    text: 'Для получения компенсации в случае повреждения, частичной или полной утраты груза необходимо подать претензию. ',
    link: {
      title: 'Подробнее о необходимых документах, процессе подачи претензии и её рассмотрении.',
      to: '/claim-filing/'
    }
  }
}

const wrappingItem = {
  header: 'Как упаковать груз?',
  component: HelpWrapping
}

const dangerousItem = computed(() => {
  return {
    header: 'Какие опасные грузы не принимаются к перевозке?',
    component: HelpText,
    param: {
      link: {
        title: 'Список опасных грузов, которые не принимаются к перевозке',
        to: { name: 'prohibited-goods' }
      },
    }
  }
})

const corporateItems = computed(() => {
  const result = [
    {
      header: 'Какие документы нужны для сдачи груза?',
      component: HelpDocuments,
      param: corporateDeliveryData,
    },
    {
      header: 'Какие документы нужны для получения груза?',
      component: HelpDocuments,
      param: corporateReceiptData
    },
    {
      header: 'Как рассчитать стоимость и оформить заказ?',
      component: HelpServices,
      param: true
    },
    {
      header: 'Как добавить/отменить дополнительную услугу?',
      component: HelpServices
    },
    {
      header: 'Как оплатить перевозку?',
      component: HelpText,
      param: {
        text: 'Доступны способы оплаты онлайн и на терминале. ',
        link: {
          title: 'Подробнее',
          to: '/payment-methods/'
        }
      }
    },
    { ...wrappingItem },
    {
      header: 'Как застраховать груз?',
      component: HelpText,
      param: {
        text: 'Компания предоставляет услуги традиционного и потокового страхования. ',
        link: {
          title: 'Подробнее.',
          to: '/insurance/'
        }
      }
    },
    {
      header: 'Что такое возврат сопроводительных документов?',
      component: HelpText,
      param: {
        text:  'Дополнительная услуга «Возврат сопроводительных документов» позволяет отправить вместе с грузом сопроводительные документы и быстро получить их назад, с подписью и печатью Получателя. ',
        link: {
          title: 'Подробнее об услуге',
          to: '/return-sd/'
        }
      }
    },
    {
      header: 'Как подключить электронный документооборот (ЭДО)?',
      component: HelpText,
      param: {
        text: 'Vozovoz работает по ЭДО с операторами - Сбис и Диадок. ',
        link: {
          title: 'Подробнее.',
          to: '/edo/'
        }
      }
    },
    { ...claimItem },
    {
      header: 'Как получить полный доступ к контрагенту в личном кабинете?',
      component: HelpText,
      param: {
        text: 'Заполнить заявление на доступ по кнопке «Получить полный доступ» в профиле клиента во вкладке «Контрагенты», после этого прикрепить скан-копию заполненного заявления по этой же кнопке.'
      }
    },
    { ...dangerousItem.value }
  ]

  if (isHideInsurance) {
    const findInsurance = result.findIndex((val) => val.param?.link?.to === '/insurance/')
    result.splice(findInsurance, 1)
  }

  return result
})

const individualItems = computed(() => {
  return [
    {
      header: 'Какие документы нужны для сдачи груза?',
      component: HelpList,
      param: individualDeliveryData
    },
    {
      header: 'Какие документы нужны для получения груза?',
      component: HelpText,
      param: {
        text: 'Код получения - набор цифр, который направляется по SMS контактному лицу, данные которого указаны в заказе.'
      }
    },
    {
      header: 'Как рассчитать стоимость и оформить заказ?',
      component: HelpServices,
      param: true
    },
    {
      header: 'Как добавить/отменить дополнительную услугу?',
      component: HelpServices
    },
    {
      header: 'Что такое «код получения»?',
      component: HelpText,
      param: {
        text: 'Код получения - это набор цифр, необходимый для получения груза. Он направляется по SMS контактному лицу, данные которого указаны в заказе.'
      }
    },
    { ...claimItem },
    {
      header: 'Как оплатить перевозку?',
      component: HelpText,
      param: {
        text: 'Доступны способы оплаты онлайн и на терминале, а также наличными. ',
        link: {
          title: 'Подробнее',
          to: '/payment-methods/'
        }
      }
    },
    { ...wrappingItem },
    {
      header: 'Что такое погрузо-разгрузочные работы?',
      component: HelpText,
      param: {
        text: 'Погрузо-разгрузочные работы (ПРР) - это транспортировка груза клиента на адресе забора/доставки или на терминале компании. В ПРР входят услуги грузчиков и специальной техники для грузов с нестандартными габаритами. ',
        link: {
          title: 'Подробнее.',
          to: '/cargo-loading/',
        }
      }
    },
    { ...dangerousItem.value }
  ]
})

const items = computed(() => {
  return props.mode === 'corporate' ? corporateItems.value : individualItems.value
})

function hasItempropQuestion(componentName: string) {
  return [HelpText, HelpWrapping].includes(componentName)
}

onMounted( async(): Promise<void> => {
  const doc = await getDocuments({documents: ['danger-cargo-vz']})
  dangerousCargoDoc.value = doc['danger-cargo-vz']?.fileUrl || doc['danger-cargo-vz']?.url || '/documents/'
})
</script>
